import { type ReactNode } from 'react';

import { Link as AriaLink, LinkProps as AriaLinkProps } from 'react-aria-components';

import { css } from '@allenai/varnish-panda-runtime/css';

import { cx } from '@/utils/cx';

import buttonRecipe, { ButtonRecipeProps } from '@/components/button/button.styles';
import focusRing from '@/components/shared/focusRing.styles';

/**
 *  On the surface, building a custom styled button seems simple. However, there are many cross
 *  browser inconsistencies in interactions and accessibility features to consider. Button handles
 *  all of these interactions for you, so you can focus on the styling.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/Button.html
 */

type ButtonLinkProps = {
    className?: string;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    buttonAriaLabel?: string;
    children?: ReactNode;
    startIconClassName?: string;
    endIconClassName?: string;
} & AriaLinkProps &
    ButtonRecipeProps;

/**
 * Button component that renders a button with customizable styles based on variant, size, and color.
 *
 * @param props The props for the button including variant, size, color, and additional HTML button props.
 *
 * @returns A button element with the combined styles from the MUI base button and the custom variants.
 */
const ButtonLink = ({
    className,
    startIconClassName,
    endIconClassName,
    startIcon,
    endIcon,
    buttonAriaLabel,
    children,
    ...rest
}: ButtonLinkProps) => {
    const [variantProps, localProps] = buttonRecipe.splitVariantProps(rest);
    const recipeClassNames = buttonRecipe(variantProps);

    return (
        <AriaLink
            className={cx(css(focusRing), recipeClassNames.root, className)}
            aria-label={buttonAriaLabel}
            {...localProps}>
            {startIcon ? (
                <span className={cx(recipeClassNames.startIcon, startIconClassName)}>
                    {startIcon}
                </span>
            ) : null}
            {children}
            {endIcon ? (
                <span className={cx(recipeClassNames.endIcon, endIconClassName)}>{endIcon}</span>
            ) : null}
        </AriaLink>
    );
};

export { ButtonLink, ButtonLink as default };
export type { ButtonLinkProps };
